import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { WreathDesignId } from '@forbiz/contracts'

import { Chrysanthemum, WreathDesignPicker } from '../components/WreathDesign.js'
import { WREATH_DESIGNS, digitalWreathApi, getWreathDesign } from '../lib/digitalWreath.js'
import s from './DigitalWreathPage.module.css'

/**
 * 내 디지털 화환 — 설정 페이지 (BIZ-125. 옛 이름 '디지털 근조기' — 근조기는 별도 판매 상품으로 분리).
 *
 * 디자인을 골라 보유해 두면, 화환 주문서에서 배송지가 제휴 장례식장일 때 이 디자인으로
 * 부고장에 함께 올라간다(주문서가 이 설정을 기본값으로 읽고, 주문서에서 바꾸면 접수 때 여기에 저장된다). **디자인은 부고장 카드에만
 * 적용된다** — 장례식장 화면은 자막(텍스트) 티커라 디자인이 없고, 그래서 미리보기도
 * 부고장 중심 + 자막 보조 스트립으로 나눈다. 저장은 서버(회사 귀속, BIZ-126) — 무료 부가 서비스.
 */

export function DigitalWreathPage() {
  const navigate = useNavigate()
  const [designId, setDesignId] = useState<WreathDesignId>(WREATH_DESIGNS[0]!.id)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  // 서버에 저장된 내 디자인을 불러온다. 실패·미설정이면 기본 디자인 유지.
  useEffect(() => {
    let alive = true
    digitalWreathApi
      .get()
      .then((r) => {
        if (alive && r.designId) setDesignId(r.designId)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  const design = getWreathDesign(designId)
  const sampleRibbon = '삼가 고인의 명복을 빕니다'
  const sampleSender = '(주)포비즈'
  const barGrad = `linear-gradient(180deg, ${design.bar[0]} 0%, ${design.bar[1]} 100%)`

  async function save() {
    if (busy) return
    setBusy(true)
    try {
      await digitalWreathApi.update(designId)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2200)
    } catch {
      // 실패는 조용히 — 버튼이 원상태로 돌아와 다시 시도할 수 있다.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={s.page}>
      {/* 헤더 — 뒤로가기와 제목을 분리해 읽기 흐름을 정돈 */}
      <button type="button" className={s.back} onClick={() => navigate('/')}>
        ← 홈
      </button>
      <h1 className={s.title}>내 디지털 화환</h1>
      <p className={s.sub}>
        디자인을 골라두면, 제휴 장례식장으로 화환을 보낼 때 부고장에 이 디지털 화환이 함께 올라갑니다.
      </p>

      {/* 부고장 미리보기 — 디자인이 적용되는 곳 */}
      <section className={s.previewCard} aria-label="부고장 미리보기">
        <div className={s.previewLabel}>부고장에 이렇게 올라가요</div>
        <div className={s.obituaryFrame}>
          <div className={s.obituarySection}>근조화환 조문해주신 분</div>
          <div className={s.wreathCard}>
            <span className={s.wreathBar} style={{ background: barGrad }} aria-hidden="true" />
            <div className={s.wreathInner}>
              <span className={s.wreathCircle} style={{ background: design.circleBg, color: design.flower }}>
                <Chrysanthemum className={s.wreathFlower} />
              </span>
              <div className={s.wreathMsg}>{sampleRibbon}</div>
              <div className={s.wreathSender}>{sampleSender}</div>
            </div>
            <span className={s.wreathBar} style={{ background: barGrad }} aria-hidden="true" />
          </div>
        </div>

        {/* 장례식장 화면 — 자막이라 디자인 무관. 보조 스트립으로 정직하게. */}
        <div className={s.stripWrap}>
          <div className={s.stripLabel}>
            장례식장 화면에는 <b>자막</b>으로 나가요
          </div>
          <div className={s.displayStrip} aria-label="장례식장 자막 미리보기">
            <div className={s.tickerTrack}>
              {[0, 1].map((k) => (
                <span key={k} className={s.tickerItem}>
                  {sampleRibbon} — {sampleSender}
                  <i className={s.tickerDot} aria-hidden="true" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 디자인 선택 */}
      <section className={s.designSection}>
        <div className={s.designLabel}>디자인</div>
        <WreathDesignPicker value={designId} onChange={setDesignId} />
      </section>

      <button type="button" className={s.cta} onClick={() => void save()} disabled={busy}>
        {saved ? '저장했어요 ✓' : busy ? '저장 중…' : '이 디자인으로 저장'}
      </button>
      <p className={s.mockNote}>
        제휴 장례식장으로 화환을 보낼 때 이 디자인이 기본으로 선택돼요 · 추가 비용 없음
      </p>
    </div>
  )
}
